import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSupabase } from '../../../contexts/SupabaseContext';
import { PieChart } from '@mui/x-charts/PieChart';
import { chartPalette } from '../../../theme';

const EquityOthers = () => {
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);

  const fetchInvestments = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('investments')
        .select('id, name, value')
        .eq('user_id', user.id)
        .eq('type', 'equity_others');

      if (error) throw error;

      const transformedData = data.map(item => ({
        id: item.id,
        name: item.name,
        value: Number(item.value) || 0
      }));

      setInvestments(transformedData);
      const total = transformedData.reduce((sum, item) => sum + item.value, 0);
      setTotalValue(total);

    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvestments();
  }, [user]);

  const pieChartData = investments.map(item => ({
    id: item.id,
    value: item.value,
    label: item.name
  }));

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton 
          onClick={() => navigate('/investments/equity')}
          sx={{ 
            color: 'text.secondary',
            '&:hover': { 
              color: 'text.primary',
              bgcolor: 'action.hover' 
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Other Equity Investments
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Total: {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
              maximumFractionDigits: 0
            }).format(totalValue)}
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : investments.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          p: 4,
          color: 'text.secondary',
          textAlign: 'center'
        }}>
          <Typography variant="h6">No Other Investments Found</Typography>
          <Typography variant="body2">
            No other equity investments found. Start investing to see them here.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ borderRadius: 2 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Investment Name</TableCell>
                      <TableCell align="right">Value</TableCell>
                      <TableCell align="right">Allocation</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {investments.map((investment) => (
                      <TableRow key={investment.id}>
                        <TableCell>{investment.name}</TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0
                          }).format(investment.value)}
                        </TableCell>
                        <TableCell align="right">
                          {((investment.value / totalValue) * 100).toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ height: '100%', borderRadius: 2, p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Distribution</Typography>
              {pieChartData.length > 0 && (
                <PieChart colors={chartPalette}
                  series={[{
                    data: pieChartData,
                    innerRadius: 60,
                    outerRadius: 110,
                    paddingAngle: 2,
                    cornerRadius: 4,
                    startAngle: -90,
                    endAngle: 270
                  }]}
                  height={300}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default EquityOthers;